<template>
  <span :class="rootClasses">
    <Tooltip v-if="tooltipText" width="300" placement="top">
      <template #trigger>
        <div :class="contentLayoutClass">
          <span class="sera-badge__text">{{ props.text }}</span>
          <i
            v-if="props.icon"
            class="sera-badge__icon fa-regular"
            :class="{ [props.icon]: true }"
          />
        </div>
      </template>
      <template #tooltip>
        <div class="text-center">
          {{ tooltipText }}
        </div>
      </template>
    </Tooltip>
    <span v-else>
      <span class="sera-badge__text">{{ props.text }}</span>
      <i
        v-if="props.icon"
        class="sera-badge__icon fa-regular"
        :class="{ [props.icon]: true }"
      />
    </span>
  </span>
</template>

<script setup lang="ts">
import { computed } from "vue";

import Tooltip from "./Tooltip.vue";

const props = defineProps({
  text: {
    type: String,
    required: true
  },
  icon: {
    type: String,
    required: false
  },
  color: {
    type: String,
    default: null
  },
  tooltipText: {
    type: String,
    required: false,
    default: null
  },
  // "chip" renders a compact, borderless status pill sized to its text —
  // used for semantic status cells (e.g. Available / Completed) in dense tables.
  // Leaving `variant` unset preserves the original bordered badge used by
  // Invoice/Quote/AccountingClassStatus/etc.
  variant: {
    type: String as () => "chip" | null,
    default: null,
    validator: (value: string) => value == null || value === "chip"
  }
});

const rootClasses = computed(() => {
  if (props.variant === "chip") {
    return [
      "sera-badge",
      "sera-badge--chip",
      `sera-badge--chip-${props.color || "gray"}`
    ];
  }
  return ["sera-badge", props.color ? `sera-badge--${props.color}` : ""];
});

const contentLayoutClass = computed(() =>
  props.variant === "chip" ? "sera-badge__layout" : "d-flex align-items-center"
);
</script>

<style lang="scss" scoped>
.sera-badge {
  display: flex;
  align-items: center;
  height: 26px;
  padding: 0px 8px;
  font-size: 14px;
  font-weight: normal;
  border-radius: 4px;
  margin-left: 16px;
  background: #f4f5f5;
  border: 1px solid #9ca4aa;

  &--gray {
    background: #f4f5f5;
    border-color: #9ca4aa;
  }
  &--red {
    background: #ffecec;
    border-color: #de0000;
  }
  &--green {
    background: #f0fff1;
    border-color: #008a09;
  }
  &--yellow {
    background: #fff9df;
    border-color: #ffda33;
  }
  &--blue {
    background: #e9f4ff;
    border-color: #125aeb;
  }

  &__text {
  }

  &__icon {
    margin-left: 8px;
  }

  // Compact chip variant — hugs content, no border, soft tonal fill.
  // The original rules above still apply to height/border-radius; the
  // chip modifier overrides layout, border, margin, and typography.
  &--chip {
    display: inline-flex;
    height: auto;
    line-height: 1.4;
    padding: 2px 10px;
    margin-left: 0;
    font-size: 12px;
    font-weight: 500;
    border: 0;
    background: transparent;
  }

  &--chip-gray {
    background: #f3f4f6;
    color: #374151;
  }
  &--chip-red {
    background: #fee2e2;
    color: #b91c1c;
  }
  &--chip-green {
    background: #d1fae5;
    color: #047857;
  }
  &--chip-yellow {
    background: #fef3c7;
    color: #b45309;
  }
  &--chip-blue {
    background: #dbeafe;
    color: #1d4ed8;
  }

  &__layout {
    display: inline-flex;
    align-items: center;
  }
}
</style>
